import { Link } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import type { GameId, QuestState } from '@tg/shared';
import { paths } from '@/app/paths';
import { GameCover } from '@/art/GameCover';
import { Emblem } from '@/art/Emblem';
import { useFormat } from '@/i18n/useFormat';
import { useLocale } from '@/i18n/useLocale';
import { Button, cx } from '@/ui';
import { buttonClasses } from '@/ui/Button';
import { gameName } from '@/features/achievements/text';
import { useQuestClaim } from './QuestListener';
import { useQuest } from './store';
import s from './quests.module.css';

const PLAY = cx(buttonClasses.btn, buttonClasses.teal, buttonClasses.solid, buttonClasses.sm, s.play);

/** progress bar + «3/10» (shared by the lobby card and the in-game chip) */
export function QuestProgress({ q, className }: { q: QuestState; className?: string }) {
  const intl = useIntl();
  const f = useFormat();
  const label = intl.formatMessage({ id: 'quests.progress' }, { done: q.done, target: q.target });
  return (
    <div className={cx(s.progress, className)}>
      <div
        className={s.bar}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={q.target}
        aria-valuenow={q.done}
        aria-label={label}
        data-done={q.done >= q.target}
      >
        <span style={{ width: `${(100 * Math.min(q.done, q.target)) / q.target}%` }} />
      </div>
      <span className={s.count} aria-hidden="true">
        {f.int(q.done)}/{f.int(q.target)}
      </span>
    </div>
  );
}

/**
 * «Daily quest» in the lobby (desktop: the right rail under the daily bonus; phone: above the sections): the game of
 * the day with its cover, «play N rounds», progress, the reward and one action — «Play», «Claim», or a quiet line
 * (claimed today; a break or a used-up limit: «the quest will wait until tomorrow», no button, no call to play).
 * Hidden for guests and while the quest is off.
 */
export function QuestCard({ compact = false }: { compact?: boolean }) {
  const q = useQuest((st) => st.quest);
  const intl = useIntl();
  const f = useFormat();
  const l = useLocale();
  const { claim, claiming } = useQuestClaim();
  if (!q) return null;
  const name = gameName(intl, q.game);
  const cover = compact ? { w: 80, h: 50 } : { w: 96, h: 60 };
  return (
    <section
      className={cx(s.card, compact && s.compact)}
      aria-label={intl.formatMessage({ id: 'quests.title' })}
      data-testid="quest-card"
      data-status={q.status}
    >
      <div className={s.head}>
        <span className={s.title}>
          <Emblem name="quest" size={compact ? 26 : 30} className={s.emblem} />
          <FormattedMessage id="quests.title" />
        </span>
        <span className={s.reward} title={intl.formatMessage({ id: 'quests.reward' })}>
          +{f.int(q.reward)}
        </span>
      </div>
      <div className={s.body}>
        <Link to={paths.game(l, q.game as GameId)} className={s.cover} tabIndex={-1} aria-hidden="true">
          <GameCover game={q.game} width={cover.w} height={cover.h} />
        </Link>
        <div className={s.text}>
          <p className={s.task}>
            <FormattedMessage id="quests.task" values={{ n: q.target, game: name }} />
          </p>
          <p className={s.note}>
            <FormattedMessage id="quests.any" values={{ min: f.int(q.minBet) }} />
          </p>
        </div>
      </div>
      <QuestProgress q={q} />
      {q.status === 'complete' && (
        <Button tone="orange" size="sm" block onClick={() => void claim()} disabled={claiming} data-testid="quest-claim">
          <FormattedMessage id="quests.claim" values={{ amount: f.int(q.reward) }} />
        </Button>
      )}
      {q.status === 'active' && !q.paused && (
        <Link to={paths.game(l, q.game as GameId)} className={PLAY}>
          <FormattedMessage id="quests.play" />
        </Link>
      )}
      {q.status === 'active' && q.paused && (
        <p className={s.quiet} data-testid="quest-paused">
          <FormattedMessage id="quests.paused" />
        </p>
      )}
      {q.status === 'claimed' && (
        <p className={s.quiet}>
          <FormattedMessage id="quests.claimed" />
        </p>
      )}
    </section>
  );
}

/** The quest mark (a flag): stands in for the «Quest» label on a narrow desktop header (CSS swaps them). */
function QuestMark() {
  return (
    <svg className={s.chipIcon} width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <path d="M2.5 1v10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
      <path d="M3.2 1.6h6.6l-1.6 2.4 1.6 2.4H3.2z" fill="currentColor" />
    </svg>
  );
}

/**
 * The compact quest indicator in the header of a game screen — only on the game of the day: «Quest 3/10»; when done,
 * a «Claim +58» button right there. Nothing during a break / a used-up limit and after the claim (no pressure).
 */
export function QuestChip({ game, small }: { game: string | undefined; small?: boolean }) {
  const q = useQuest((st) => st.quest);
  const intl = useIntl();
  const f = useFormat();
  const { claim, claiming } = useQuestClaim();
  if (!q || !game || q.game !== game || q.status === 'claimed' || (q.status === 'active' && q.paused)) return null;
  if (q.status === 'complete') {
    return (
      <button
        type="button"
        className={cx(s.chip, s.chipClaim, small && s.chipSm)}
        onClick={() => void claim()}
        disabled={claiming}
        data-testid="quest-chip"
      >
        <QuestMark />
        <span className={s.chipLabel}>
          <FormattedMessage id="quests.chip.label" />
        </span>
        <span className={s.chipValue}>
          <FormattedMessage id="quests.chip.claim" values={{ amount: f.int(q.reward) }} />
        </span>
      </button>
    );
  }
  return (
    <span
      className={cx(s.chip, small && s.chipSm)}
      role="status"
      aria-label={intl.formatMessage({ id: 'quests.chip.aria' }, { done: q.done, target: q.target })}
      title={intl.formatMessage({ id: 'quests.task' }, { n: q.target, game: gameName(intl, q.game) })}
      data-testid="quest-chip"
    >
      <QuestMark />
      <span className={s.chipLabel} aria-hidden="true">
        <FormattedMessage id="quests.chip.label" />
      </span>
      <span className={s.chipBar} aria-hidden="true">
        <span style={{ width: `${(100 * q.done) / q.target}%` }} />
      </span>
      <span className={s.chipValue} aria-hidden="true">
        {f.int(q.done)}/{f.int(q.target)}
      </span>
    </span>
  );
}
